import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Photo, SkeletonLoader } from '../';
import { useMessage, useTooltip } from '../../hooks';
import { Asset, AssetName } from '../../helpers';

import { principalService, userService } from '../../services';

import './PrincipalLink.css';

/**
 * Render a link to a principal.
 * Supports fetching data if only principalId or userId is provided (and fetchPrincipalData = true), will otherwise try to render the link based on given data.
 * userId and principalId should not both be specified, pick one (principalId is recommended).
 * */
export const PrincipalLink = ({
  principalId,
  userId,
  principalPhoto,
  principalName,
  principalType,
  userTitle,
  userDepartment,
  fetchPrincipalData = true,
  className,
  header,
  photoPosition = 'left',
  type = 'USER', // Can be USER, USER_GROUP or SERVICE_ACCOUNT. Controls the type of placeholder image if a photo is missing.
  tooltip = '',
  loading = false,
}) => {
  const [currentValue, setCurrentValue] = useState();
  const [loadingInternal, setLoadingInternal] = useState(
    !!(principalId || userId) && fetchPrincipalData
  );

  const { showMessage } = useMessage();
  const { showTooltip, hideTooltip } = useTooltip();

  useEffect(() => {
    // Fetch either principal or user
    if (principalId && fetchPrincipalData && currentValue?.principal_id !== principalId) {
      fetchPrincipal();
    } else if (userId && fetchPrincipalData && currentValue?.user_id !== userId) {
      fetchUser();
    }
  }, [principalId, userId]);

  loading = loading || loadingInternal;
  principalName =
    currentValue?.principal_name || principalName || (!loading ? 'Unknown' : undefined);
  principalPhoto = currentValue?.principal_photo || principalPhoto;
  principalType = currentValue?.principal_type || principalType;
  let principalUsername =
    principalType && principalType === Asset.User
      ? currentValue?.principal_username || AssetName[Asset.User]
      : AssetName[Asset.UserGroup];
  let principalDescription = currentValue?.principal_description;
  const principalUrl =
    principalType && (currentValue?.object_id || principalId || userId)
      ? `/${principalType + 's'}/${currentValue?.object_id || principalId || userId}`
      : undefined;

  if (principalType === Asset.User && principalDescription) {
    const parts = principalDescription.split(',').map(x => x.trim());
    userTitle = userTitle || (parts[0].length > 0 && parts[0]);
    userDepartment = userDepartment || (parts[1].length > 0 && parts[1]);
    principalDescription = undefined;
  }

  tooltip = tooltip || principalName;

  const fetchPrincipal = async () => {
    setLoadingInternal(true);
    principalService
      .getById(principalId)
      .then(res => {
        res && setCurrentValue(res.principals[0]);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => setLoadingInternal(false));
  };

  const fetchUser = async () => {
    setLoadingInternal(true);
    userService
      .getById(userId)
      .then(res => {
        const user = res && res.users[0];
        // Add user as principal data
        user &&
          setCurrentValue({
            principal_id: user.principal_id,
            principal_type: 'user',
            object_id: user.user_id,
            principal_source_id: user.user_source_id,
            principal_source: user.user_auth_method,
            principal_username: user.user_username,
            principal_name: user.user_fullname,
            principal_email: user.user_email,
            principal_description: [user.user_title, user.user_department].join(', '),
            principal_photo: user.user_photo,
          });
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => setLoadingInternal(false));
  };

  const tooltipBody = (
    <div className="user-profile slim">
      <Photo size="medium" data={principalPhoto} name={principalName} loading={loading} />
      <div className="user-profile-info">
        <div className="user-profile-name">{principalName}</div>
        {principalUsername && <div className="user-profile-username">{principalUsername}</div>}
        {userTitle && <div className="user-profile-title">{userTitle}</div>}
        {userDepartment && <div className="user-profile-title">{userDepartment}</div>}
        {principalDescription && <div className="user-profile-title">{principalDescription}</div>}
      </div>
    </div>
  );

  return (
    <div className={'PrincipalLink' + ' photo-' + photoPosition + (header ? ' with-header' : '')}>
      {!(loading || !principalName) ? (
        <React.Fragment>
          {principalUrl ? (
            <Link
              to={principalUrl}
              className={'link principal-link-body' + (className ? ' ' + className : '')}
              onMouseEnter={() => tooltip && showTooltip({ tooltipBody: tooltipBody })}
              onMouseLeave={() => tooltip && hideTooltip()}
            >
              <Photo
                data={principalPhoto}
                name={principalName}
                size="small"
                type={type}
                loading={loading}
                alt={principalName}
              />
              <div className="principal-link">
                {header && <div className="principal-link-header">{header}</div>}
                <div className="principal-link-name">{principalName}</div>
              </div>
            </Link>
          ) : (
            <div className={'principal-link-body' + (className ? ' ' + className : '')}>
              <Photo
                data={principalPhoto}
                name={principalName}
                size="small"
                type={type}
                loading={loading}
                alt={principalName}
              />
              <div className="principal-link">
                {header && <div className="principal-link-header">{header}</div>}
                <div className="principal-link-name">{principalName}</div>
              </div>
            </div>
          )}
        </React.Fragment>
      ) : (
        <div className={'principal-link-body' + (className ? ' ' + className : '')}>
          <Photo
            data={principalPhoto}
            name={principalName}
            size="small"
            type={type}
            loading={loading}
            alt={principalName}
          />
          <div className="principal-link">
            {header && (
              <div className="principal-link-header">
                <SkeletonLoader width="130px" />
              </div>
            )}
            <div className="principal-link-name">
              <SkeletonLoader width="130px" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
